import { createStore, useStore } from 'zustand'

/**
 * Cross-tree state for the by-post posts sidepanel pill: how many comments
 * have been ingested, on how many distinct posts, since the panel last loaded
 * its list.
 *
 * The posts list no longer reloads when a comment arrives, so its order holds
 * still and the pill is what offers the new state. The two sides sit in
 * different parts of the tree — the ingestion events are observed by the posts
 * query hook, which knows the channels the list covers, while the pill renders
 * inside the sidepanel — so this module carries the tally between them.
 *
 * Both figures stop growing at the point the pill's copy caps them, so a
 * session that receives comments for hours holds a bounded amount of state.
 */
/** The tally at a point in time, so a reload can drop exactly what it covered. */
export type PendingPostCommentsSnapshot = {
  commentCount: number
  postIds: ReadonlySet<string>
}

type PendingPostCommentsState = {
  commentCount: number
  postIds: ReadonlySet<string>
  countedCommentIds: ReadonlySet<string>
  registerComment: (postId: string, commentId?: string) => void
  forget: (covered: PendingPostCommentsSnapshot) => void
  clear: () => void
}

/** One past the highest comment count the pill spells out ("99+"). */
const MAX_TRACKED_COMMENTS = 100

/** One past the highest post count the pill spells out ("9+"). */
const MAX_TRACKED_POSTS = 10

/**
 * How many comment ids stay remembered to suppress repeat events.
 *
 * Comfortably more than the tally itself can reach, so a comment counted while
 * the pill is up is still recognised if its event repeats.
 */
const MAX_DEDUPE_IDS = 200

const EMPTY_POST_IDS: ReadonlySet<string> = new Set()

/**
 * Adds an id, dropping the oldest once the window is full. A `Set` iterates in
 * insertion order, so its first key is the oldest id.
 */
const rememberCommentId = (
  ids: ReadonlySet<string>,
  commentId: string,
): ReadonlySet<string> => {
  const next = new Set(ids)
  next.add(commentId)
  while (next.size > MAX_DEDUPE_IDS) {
    const oldest = next.values().next().value
    if (oldest === undefined) break
    next.delete(oldest)
  }
  return next
}

const pendingPostCommentsStore = createStore<PendingPostCommentsState>()(
  (set, get) => ({
    commentCount: 0,
    postIds: EMPTY_POST_IDS,
    countedCommentIds: EMPTY_POST_IDS,

    registerComment: (postId, commentId): void => {
      const { commentCount, postIds, countedCommentIds } = get()

      // The producer emits an ingest event for the same comment more than
      // once, so counting every event overstates what arrived. A comment is
      // only counted once, keyed on its id. An event carrying no id cannot be
      // recognised, so it is counted rather than dropped: a tally that runs
      // slightly high is closer to the truth than one that silently misses
      // comments.
      if (commentId !== undefined && countedCommentIds.has(commentId)) return

      const nextCommentCount = Math.min(commentCount + 1, MAX_TRACKED_COMMENTS)
      const trackPost = !postIds.has(postId) && postIds.size < MAX_TRACKED_POSTS
      const nextCountedCommentIds =
        commentId === undefined
          ? countedCommentIds
          : rememberCommentId(countedCommentIds, commentId)

      if (
        nextCommentCount === commentCount &&
        !trackPost &&
        nextCountedCommentIds === countedCommentIds
      ) {
        return
      }

      set({
        commentCount: nextCommentCount,
        postIds: trackPost ? new Set([...postIds, postId]) : postIds,
        countedCommentIds: nextCountedCommentIds,
      })
    },

    forget: (covered): void => {
      const { commentCount, postIds } = get()
      const nextCommentCount = Math.max(commentCount - covered.commentCount, 0)
      const nextPostIds = new Set(
        [...postIds].filter((postId) => !covered.postIds.has(postId)),
      )

      if (
        nextCommentCount === commentCount &&
        nextPostIds.size === postIds.size
      ) {
        return
      }

      set({
        commentCount: nextCommentCount,
        postIds: nextPostIds.size === 0 ? EMPTY_POST_IDS : nextPostIds,
      })
    },

    // Resets the repeat-suppressing ids along with the tally: they are that
    // tally's bookkeeping, and the list they describe is gone. A reload keeps
    // them instead, via `forget`, because repeat events do arrive either side
    // of one.
    clear: (): void => {
      const { commentCount, postIds, countedCommentIds } = get()
      if (
        commentCount === 0 &&
        postIds.size === 0 &&
        countedCommentIds.size === 0
      ) {
        return
      }
      set({
        commentCount: 0,
        postIds: EMPTY_POST_IDS,
        countedCommentIds: EMPTY_POST_IDS,
      })
    },
  }),
)

/**
 * Record an ingested comment against the post it was left on.
 *
 * Pass the comment's id whenever the event carries one: it is what keeps a
 * comment from being counted twice when its event repeats.
 */
export const registerPendingPostComment = (
  postId: string,
  commentId?: string,
): void =>
  pendingPostCommentsStore.getState().registerComment(postId, commentId)

/**
 * Forget the tally, e.g. once the list has been reloaded, or when the list it
 * describes is replaced by a different one.
 */
export const clearPendingPostComments = (): void =>
  pendingPostCommentsStore.getState().clear()

/**
 * Copy the tally so a reload can later drop what it covered. Comments that
 * arrive while the reload runs are not in the copy, so they survive it.
 */
export const takePendingPostCommentsSnapshot =
  (): PendingPostCommentsSnapshot => {
    const { commentCount, postIds } = pendingPostCommentsStore.getState()
    return { commentCount, postIds: new Set(postIds) }
  }

/** Drop the comments a reload covered, keeping any that arrived while it ran. */
export const forgetPendingPostComments = (
  covered: PendingPostCommentsSnapshot,
): void => pendingPostCommentsStore.getState().forget(covered)

/** Read the tally without subscribing. */
export const getPendingPostComments = (): {
  commentCount: number
  postCount: number
} => {
  const { commentCount, postIds } = pendingPostCommentsStore.getState()
  return { commentCount, postCount: postIds.size }
}

/** How many comments have arrived since the list was loaded. */
export const usePendingPostCommentsCount = (): number =>
  useStore(pendingPostCommentsStore, (state) => state.commentCount)

/** How many distinct posts those comments were left on. */
export const usePendingPostCommentsPostCount = (): number =>
  useStore(pendingPostCommentsStore, (state) => state.postIds.size)
